// Modal de upload e crop de foto do técnico.
//
// Uso:
//   <UploadFotoModal tecnicoId={t.id} fotoAtual={t.foto_url}
//                    onSalvo={(novaUrl) => setCampo('foto_url', novaUrl)}
//                    onFechar={() => setMostrarModal(false)} />
//
// Fluxo:
//  1) usuário clica "Selecionar arquivo"
//  2) imagem carrega, mostra crop circular (react-easy-crop)
//  3) ajusta zoom + posição
//  4) clica "Salvar" → gera blob cropado em canvas → POST multipart
//  5) backend retorna nova foto_url → onSalvo()

import { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import api from '../lib/api.js';

const TAMANHO_OUTPUT = 512;   // tamanho final que o front gera (backend reduz pra 256)

export default function UploadFotoModal({ tecnicoId, fotoAtual, onSalvo, onFechar }) {
  const [imgSrc, setImgSrc] = useState(null);   // dataURL da imagem carregada
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const inputRef = useRef(null);

  const onCropComplete = useCallback((croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  function abrirSelector() {
    inputRef.current?.click();
  }

  function onSelecionarArquivo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Selecione uma imagem (JPG, PNG ou WebP)');
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      alert('Imagem muito grande (limite 12 MB). Use uma menor.');
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => setImgSrc(ev.target?.result);
    reader.readAsDataURL(file);
  }

  // Recorta a área selecionada usando canvas e retorna Blob
  async function gerarBlobCropado() {
    if (!imgSrc || !croppedAreaPixels) return null;
    const img = await carregarImg(imgSrc);
    const canvas = document.createElement('canvas');
    canvas.width  = TAMANHO_OUTPUT;
    canvas.height = TAMANHO_OUTPUT;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      img,
      croppedAreaPixels.x, croppedAreaPixels.y,
      croppedAreaPixels.width, croppedAreaPixels.height,
      0, 0, TAMANHO_OUTPUT, TAMANHO_OUTPUT
    );
    return await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.9));
  }

  async function salvar() {
    if (!croppedAreaPixels) return;
    setSalvando(true);
    try {
      const blob = await gerarBlobCropado();
      const fd = new FormData();
      fd.append('foto', blob, 'foto.jpg');
      const r = await api.post(`/v1/tecnicos/${tecnicoId}/foto`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSalvo(r.data.foto_url);
    } catch (e) {
      alert('Falha ao salvar: ' + (e.uiMessage || e.message));
    } finally {
      setSalvando(false);
    }
  }

  async function removerFoto() {
    if (!confirm('Remover a foto atual? O técnico voltará a ter avatar de iniciais.')) return;
    setSalvando(true);
    try {
      await api.delete(`/v1/tecnicos/${tecnicoId}/foto`);
      onSalvo(null);
    } catch (e) {
      alert('Falha ao remover: ' + (e.uiMessage || e.message));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div onClick={onFechar} style={modalBg}>
      <div onClick={e => e.stopPropagation()} style={modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>📷 Foto do técnico</h3>
          <button onClick={onFechar} style={btnFechar}>×</button>
        </div>

        {!imgSrc ? (
          // Tela inicial: mostra foto atual + botão pra selecionar
          <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
            {fotoAtual ? (
              <img src={fotoAtual}
                style={{ width: 160, height: 160, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--cinza-borda)', marginBottom: 16 }}
                alt="Foto atual" />
            ) : (
              <div style={{ width: 160, height: 160, borderRadius: '50%', background: '#f3f4f6', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, color: '#9ca3af', fontSize: 48 }}>
                👤
              </div>
            )}
            <div style={{ fontSize: 13, color: 'var(--txt-fraco)', marginBottom: 16 }}>
              {fotoAtual ? 'Foto atual do técnico' : 'Sem foto · usando iniciais'}
            </div>
            <input ref={inputRef} type="file" accept="image/*" onChange={onSelecionarArquivo} style={{ display: 'none' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn" onClick={abrirSelector}>
                {fotoAtual ? '📤 Trocar foto' : '📤 Selecionar foto'}
              </button>
              {fotoAtual && (
                <button className="btn secondary" onClick={removerFoto} disabled={salvando}
                  style={{ color: '#dc2626', borderColor: '#fca5a5' }}>
                  🗑 Remover
                </button>
              )}
            </div>
          </div>
        ) : (
          // Tela de crop
          <>
            <div style={{ position: 'relative', width: '100%', height: 360, background: '#111', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
              <Cropper
                image={imgSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: 'var(--txt-fraco)', display: 'block', marginBottom: 4 }}>
                Zoom
              </label>
              <input type="range" min={1} max={3} step={0.05}
                value={zoom} onChange={e => setZoom(Number(e.target.value))}
                style={{ width: '100%' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn secondary" onClick={() => setImgSrc(null)} disabled={salvando}>
                ← Outra imagem
              </button>
              <button className="btn" onClick={salvar} disabled={salvando || !croppedAreaPixels}>
                {salvando ? 'Salvando...' : '💾 Salvar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function carregarImg(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

const modalBg = {
  position: 'fixed', inset: 0, background: '#0008',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
};
const modalBox = {
  background: '#fff', borderRadius: 10,
  padding: '1.25rem 1.5rem',
  width: 480, maxWidth: '90vw',
  boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
};
const btnFechar = {
  background: 'none', border: 'none', fontSize: '1.5rem',
  cursor: 'pointer', color: '#6b7280',
};
